import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseOtpCooldownResult {
  secondsLeft: number;
  isCooling: boolean;
  start: (seconds: number) => void;
  startUntil: (cooldownUntil: string | Date) => void;
  reset: () => void;
}

const storagePrefix = 'evolveos.admin.otp-cooldown.';

function readExpiry(key: string): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(storagePrefix + key);
    if (!raw) return null;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeExpiry(key: string, expiryMs: number | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (expiryMs === null || expiryMs <= Date.now()) {
      window.sessionStorage.removeItem(storagePrefix + key);
    } else {
      window.sessionStorage.setItem(storagePrefix + key, String(expiryMs));
    }
  } catch {
    /* ignore */
  }
}

function computeSecondsLeft(expiryMs: number | null): number {
  if (!expiryMs) return 0;
  return Math.max(0, Math.ceil((expiryMs - Date.now()) / 1000));
}

export function useOtpCooldown(key: string): UseOtpCooldownResult {
  const [secondsLeft, setSecondsLeft] = useState<number>(() =>
    computeSecondsLeft(readExpiry(key)),
  );
  const expiryRef = useRef<number | null>(readExpiry(key));

  useEffect(() => {
    expiryRef.current = readExpiry(key);
    setSecondsLeft(computeSecondsLeft(expiryRef.current));
  }, [key]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = window.setInterval(() => {
      const left = computeSecondsLeft(expiryRef.current);
      setSecondsLeft(left);
      if (left <= 0) {
        writeExpiry(key, null);
        expiryRef.current = null;
        window.clearInterval(id);
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [secondsLeft, key]);

  const start = useCallback(
    (seconds: number) => {
      const sanitized = Math.max(0, Math.floor(seconds || 0));
      if (sanitized <= 0) {
        writeExpiry(key, null);
        expiryRef.current = null;
        setSecondsLeft(0);
        return;
      }
      const expiry = Date.now() + sanitized * 1000;
      expiryRef.current = expiry;
      writeExpiry(key, expiry);
      setSecondsLeft(sanitized);
    },
    [key],
  );

  const startUntil = useCallback(
    (cooldownUntil: string | Date) => {
      const expiry =
        cooldownUntil instanceof Date ? cooldownUntil.getTime() : Date.parse(cooldownUntil);
      if (!Number.isFinite(expiry) || expiry <= Date.now()) {
        writeExpiry(key, null);
        expiryRef.current = null;
        setSecondsLeft(0);
        return;
      }
      expiryRef.current = expiry;
      writeExpiry(key, expiry);
      setSecondsLeft(computeSecondsLeft(expiry));
    },
    [key],
  );

  const reset = useCallback(() => {
    writeExpiry(key, null);
    expiryRef.current = null;
    setSecondsLeft(0);
  }, [key]);

  return {
    secondsLeft,
    isCooling: secondsLeft > 0,
    start,
    startUntil,
    reset,
  };
}

export function seedOtpCooldown(
  cooldown: UseOtpCooldownResult,
  hints: { cooldownSeconds?: number; cooldownUntil?: string; retryAfterSeconds?: number },
): void {
  if (hints.cooldownUntil) {
    cooldown.startUntil(hints.cooldownUntil);
    return;
  }
  const seconds = hints.cooldownSeconds ?? hints.retryAfterSeconds;
  if (typeof seconds === 'number' && seconds > 0) {
    cooldown.start(seconds);
  }
}

export function otpCooldownKey(
  channel: 'email' | 'forgot-email',
  destination: string,
): string {
  const normalized = destination.trim().toLowerCase();
  return `${channel}:${normalized}`;
}
